/**
 * @module components/auth/LoginScreen
 * @summary Two-column login screen (collapses to one column on phones).
 */
import { useState } from 'react';
import { tryLogin } from '../../lib/auth';
import { useAuthStore } from '../../state/authStore';

export function LoginScreen() {
  const setUser = useAuthStore((s) => s.setUser);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  /**
   * Chrome/Edge autofill saved credentials into a sign-in form on load, which
   * would put the shared superadmin account on screen for anyone who opens the
   * page. Browsers skip read-only fields, so both inputs start read-only and
   * unlock on first focus — the boxes are always empty until someone types.
   */
  const [unlocked, setUnlocked] = useState(false);

  const submit = async () => {
    if (loading) return;
    setLoading(true);
    setErr('');
    try {
      const session = await tryLogin(username.trim(), password);
      if (session) {
        setUser(session);
      } else {
        setErr('Invalid username or password');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div id="login-screen">
      <div id="login-card">
        <div id="login-left">
          <div id="login-logo-tag">🏭 Jeddah Cables Company</div>
          <h1>PFD Engineering Tool</h1>
          <div className="login-tagline">
            Create, manage and review process flow diagrams for cable &amp; compounding lines.
          </div>
          <div className="login-feature">✔ 90+ engineering equipment symbols</div>
          <div className="login-feature">✔ PDF / PNG / JSON export</div>
          <div className="login-feature">✔ Works on desktop, iPad and mobile</div>
        </div>
        <div id="login-right">
          <h2>Welcome back</h2>
          <p className="login-sub">Sign in to continue to your workspace.</p>
          <label className="login-lbl" htmlFor="login-user">
            Username
          </label>
          <input
            id="login-user"
            className="login-inp"
            value={username}
            autoComplete="off"
            readOnly={!unlocked}
            onFocus={() => setUnlocked(true)}
            disabled={loading}
            onChange={(e) => setUsername(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && document.getElementById('login-pass')?.focus()}
          />
          <label className="login-lbl" htmlFor="login-pass">
            Password
          </label>
          <input
            id="login-pass"
            className="login-inp"
            type="password"
            value={password}
            /* "new-password", not "off" — browsers ignore "off" on sign-in
               forms and autofill the saved password anyway. */
            autoComplete="new-password"
            readOnly={!unlocked}
            onFocus={() => setUnlocked(true)}
            disabled={loading}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && submit()}
          />
          <div id="login-err">{err}</div>
          <button id="login-submit" type="button" disabled={loading} onClick={submit}>
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </div>
      </div>
    </div>
  );
}
